import React, { useState, useEffect, ReactNode, FC, MouseEvent } from 'react';
import { FjPortal } from '../fj-portal';
import HelpIcon from '../../../icons/help';

type TooltipProps = FjDivProps & {
  Component: ReactNode;
  delay?: number;
  override?: boolean;
  showIcon?: boolean;
  onIconClick?: () => void;
};

const TooltipContainer: FC<FjDivProps> = ({ children, ...rest }: FjDivProps) => (
  <div {...rest}>{children}</div>
);

const HelpIconWrapper: FC<FjDivProps> = (props: FjDivProps) => (
  <div {...props}>
    <HelpIcon />
  </div>
);

const TooltipVisor: FC<FjDivProps> = ({ children, ...rest }: FjDivProps) => (
  <div {...rest}>{children}</div>
);

export const FjTooltip: FC<TooltipProps> = ({
  children,
  Component,
  delay = 0,
  override = false,
  dataTestId = 'fj',
  showIcon = false,
  onIconClick,
}: TooltipProps) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [hoverTimer, setHoverTimer] = useState<NodeJS.Timeout | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    return () => {
      if (hoverTimer !== null) {
        clearTimeout(hoverTimer);
      }
    };
  }, [hoverTimer]);

  const handleMouseMove = (event: MouseEvent) => {
    setPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseEnter = () => {
    const timer = setTimeout(() => setShowTooltip(true), delay);
    setHoverTimer(timer);
  };

  const handleMouseLeave = () => {
    if (hoverTimer) clearTimeout(hoverTimer);
    setShowTooltip(false);
    setPosition({ x: 0, y: 0 });
  };

  return override ? (
    children
  ) : (
    <TooltipContainer
      className="fj-tooltip"
      data-testid={`${dataTestId}-tooltip`}
      onMouseMoveCapture={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showIcon && (
        <HelpIconWrapper
          data-testid={`${dataTestId}-tooltip-icon`}
          className="icon"
          onClick={
            onIconClick
              ? (e) => {
                  e.stopPropagation();
                  onIconClick();
                }
              : undefined
          }
          style={onIconClick ? { cursor: 'pointer' } : undefined}
        />
      )}
      <FjPortal data-testId={`${dataTestId}-tooltip`} className="fj-tooltip">
        <TooltipVisor
          style={{
            top: position.y - 55,
            left: position.x - 30,
            pointerEvents: 'none',
          }}
          data-testid={`${dataTestId}-tooltip-visor`}
          className={`visor ${showTooltip ? 'visible' : 'hidden'}`}
        >
          {Component}
        </TooltipVisor>
      </FjPortal>
    </TooltipContainer>
  );
};
